import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUserProfile } from '@store/common/selectors';
import { ConfigProvider, Button, Space, Tooltip, message } from 'antd';
import AdvanceFilterBuilderForm, {
  type AdvanceFilterBuilderFormRef,
  type FormValues,
} from '@components/filter/components/common/AdvanceFilterBuilderForm';
import { Loading } from '@components/filter/components/common/Loading';
import { getMatchedCategory } from '@components/filter/components/common/AdvanceFilterBuilderForm/utils';
import {
  getDataSource,
  parseRawCategory,
  gerResponseErrorMessage,
} from '@components/filter/components/utils';
import * as Styled from './styled';
import {
  useFilterMeta,
  useFilterValue,
  useFilterStatus,
  useOnFilterSave,
  useLoading,
  useOnChange,
  useResetFilterValue,
} from '../store';
import {
  transformFilterRulesToQuery,
  convertQueryToFilterRules,
} from '@components/filter/components/common/FilterRulesBuilder';
import { type Filter, type FilterValue } from '@components/filter/types';
import apptracker from '@utils/sensor/apptracker';
import {
  OPEN_FILTER_BUILDER,
  APPLY_FILTER_ON_FILTER_BUILDER,
  SAVE_AND_APPLY_FILTER_ON_FILTER_BUILDER,
  SAVE_FILTER_ON_FILTER_BUILDER,
} from '@src/utils/sensor/track';
import { useReadOnlyRole, useAllowCreateFilter } from '../../Management/hooks';
import { READ_ONLY_MESSAGE } from '../../constant';

const dataSource = getDataSource();

const getFilterByFormData = (data: FormValues) => {
  try {
    if (data) {
      const { category, subcategory } = parseRawCategory(data.category ?? '');
      const filter = {
        category: category,
        subcategory: subcategory,
        isHiddenFromCustomer: data.isHiddenFromCustomer,
        name: data.name,
        rule: convertQueryToFilterRules(data.query),
      } as Filter;
      return filter;
    }
  } catch (e) {
    // do nothing
  }
  return;
};

const overrideFilterRule = (
  targetFilter: Filter,
  filterValue: FilterValue,
  override?: boolean,
): Filter => {
  if (!override) return targetFilter;
  return {
    ...targetFilter,
    rule: filterValue.rule,
  };
};

const FilterDialogAdvancedPanel = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterValue, setFilterValue] = useFilterValue();
  const [loading, setLoading] = useLoading();
  const formRef = useRef<AdvanceFilterBuilderFormRef>(null);
  const useProfile = useSelector(getUserProfile);
  const filterMeta = useFilterMeta();
  const onFilterSave = useOnFilterSave();
  const onFilterChange = useOnChange();
  const resetFilterValue = useResetFilterValue();
  const readonly = useReadOnlyRole();
  const allowCreateFilter = useAllowCreateFilter(dataSource);

  const defaultQuery = useMemo(() => {
    return transformFilterRulesToQuery(filterValue.rule);
  }, [filterValue.rule]);

  const [filterStatus, setFilterStatus] = useFilterStatus();
  const isAdvanceFilterModeRef = useRef(filterStatus === 'editing-advanced');
  const isAdvanceFilterMode = isAdvanceFilterModeRef.current;

  const [emptyName, setEmptyName] = React.useState(true);
  const disabledSaving = !allowCreateFilter || readonly || emptyName;

  useEffect(() => {
    // Only send once
    apptracker(OPEN_FILTER_BUILDER, {
      mode: isAdvanceFilterModeRef.current ? 'Advanced Filter' : 'Save Filter',
    });
  }, []);

  const title = isAdvanceFilterMode ? 'Filter Builder' : 'Save Filter';
  const disableHiddenFilter = !useProfile.hidden;

  const handleOnApply = async () => {
    const formData = await formRef.current?.validateFields(['query']);
    if (!formData) return;

    const rawFilter = getFilterByFormData(formData);

    if (rawFilter) {
      const filter = overrideFilterRule(rawFilter, filterValue, !isAdvanceFilterMode);
      onFilterChange?.(filter);
      setFilterStatus('idle');

      apptracker(APPLY_FILTER_ON_FILTER_BUILDER, {
        filter: JSON.stringify(filter),
      });
    }
  };

  const handleOnCancel = () => {
    setFilterStatus('idle');
    requestAnimationFrame(resetFilterValue);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current || containerRef.current.contains(event.target as HTMLElement)) {
        return;
      }

      setFilterStatus('idle');
      requestAnimationFrame(resetFilterValue);
    };

    // Add the listener in capturing phase to make sure handleClickOutside is excuted before the click event on target element.
    // In case event.target gets removed from DOM tree after it's clicked
    // Then container.contains(event.target) condition will be false which should be avoided
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [setFilterStatus, resetFilterValue]);

  const handleOnSaveAndApply = async () => {
    const formData = await formRef.current?.validateFields();
    if (!formData) return;
    const rawFilter = getFilterByFormData(formData);

    if (rawFilter) {
      const filter = overrideFilterRule(rawFilter, filterValue, !isAdvanceFilterMode);
      setLoading(true);
      try {
        const res = await onFilterSave?.(filter);
        setFilterStatus('idle');

        if (!res) throw new Error(`Failed to save ${filter.name}`);
        message.success(`${filter.name} saved successfully`);

        onFilterChange?.({
          fid: res.fid,
          name: res.name,
          rule: res.rule,
        });

        apptracker(SAVE_AND_APPLY_FILTER_ON_FILTER_BUILDER, {
          fid: res.fid,
        });
      } catch (e: any) {
        const errMsg = gerResponseErrorMessage(e) || `Failed to save ${filter.name}`;
        message.error(errMsg);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOnSave = async () => {
    const formData = await formRef.current?.validateFields();

    if (!formData) return;
    const rawFilter = getFilterByFormData(formData);

    if (rawFilter) {
      const filter = overrideFilterRule(rawFilter, filterValue, !isAdvanceFilterMode);
      setLoading(true);
      try {
        const res = await onFilterSave?.(filter);

        if (!res) throw new Error(`Failed to save ${filter.name}`);

        apptracker(SAVE_FILTER_ON_FILTER_BUILDER, {
          fid: res.fid,
        });
        onFilterChange?.({
          fid: res.fid,
          name: res.name,
          rule: res.rule,
        });
        setFilterStatus('idle');

        message.success(`${filter.name} saved successfully`);
      } catch (e: any) {
        const errMsg = gerResponseErrorMessage(e) || `Failed to save ${filter.name}`;
        message.error(errMsg);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderButtonsForAdvanceMode = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button onClick={handleOnCancel}>Cancel</Button>
        </Space>
        <Space>
          <Button ghost type="primary" onClick={handleOnApply}>
            Apply
          </Button>
          <Tooltip title={readonly ? READ_ONLY_MESSAGE : undefined}>
            <Button disabled={disabledSaving} ghost type="primary" onClick={handleOnSaveAndApply}>
              Save & Apply
            </Button>
          </Tooltip>
        </Space>
      </div>
    );
  };

  const renderButtonsForSimpleMode = () => {
    return (
      <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={readonly ? READ_ONLY_MESSAGE : undefined}>
          <Button ghost type="primary" onClick={handleOnSave} disabled={disabledSaving}>
            Save
          </Button>
        </Tooltip>
        <Button onClick={handleOnCancel}>Cancel</Button>
      </Space>
    );
  };

  return (
    <Styled.Container ref={containerRef} style={{ maxWidth: isAdvanceFilterMode ? 960 : 560 }}>
      <ConfigProvider
        getPopupContainer={(triggerNode) =>
          containerRef.current || triggerNode?.parentElement || document.body
        }
      >
        <h3>{title}</h3>
        <AdvanceFilterBuilderForm
          ref={formRef}
          initialValues={{
            name: '',
            isHiddenFromCustomer: Boolean(useProfile?.hidden),
            query: defaultQuery,
            category: getMatchedCategory(filterValue.rule?.[0]?.[0]?.field, filterMeta),
          }}
          disableFilterBuilder={!isAdvanceFilterMode}
          disableHiddenFilter={disableHiddenFilter}
          filterMeta={filterMeta}
          dataSource={dataSource}
          onChange={(changedValue, values) => {
            if (values.name) {
              setEmptyName(false);
            } else {
              setEmptyName(true);
            }

            if (changedValue.query) {
              const newRules = convertQueryToFilterRules(changedValue.query);
              setFilterValue({
                rule: newRules,
              });
            }
          }}
        />
        <Styled.ButtonWrapper>
          {isAdvanceFilterMode ? renderButtonsForAdvanceMode() : renderButtonsForSimpleMode()}
        </Styled.ButtonWrapper>
        {loading && <Loading />}
      </ConfigProvider>
    </Styled.Container>
  );
};

export default React.memo(FilterDialogAdvancedPanel);
